import { useToast } from "../../../components/common/toasts/ToastContext";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { premiumError, premiumRequest } from "../../../services/Premium";
import useUnsavedChanges from "../../../hooks/useUnsavedChanges";
import { isProcessing } from "../constants";
import { premiumPaths } from "../navigation";
import useAsyncAction from "./useAsyncAction";
import useOrderPolling from "./useOrderPolling";
import saveOrderDraft from "../saveOrderDraft";

/** Zarządza rewizją projektu, zapisem i przejściem do podglądu po udanym zapisie. */
export default function useOrderEditor(orderId, token) {
  const [order, setOrder] = useState(null);
  const [draft, setDraft] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [documentsDirty, setDocumentsDirty] = useState(false);
  const { notify } = useToast();
  const { busy, error, setError, run } = useAsyncAction(`${token}:${orderId}`);
  const markClean = useUnsavedChanges(dirty || documentsDirty);
  const navigate = useNavigate();
  const api = useCallback(
    (suffix = "", options) =>
      premiumRequest(token, `/orders/${orderId}${suffix}`, options),
    [token, orderId],
  );
  const accept = useCallback(
    (next) => {
      markClean();
      setOrder(next);
      setDraft(next.draft);
      setDirty(false);
      setDocumentsDirty(false);
    },
    [markClean],
  );
  useEffect(() => {
    const controller = new AbortController();
    setOrder(null);
    setDraft(null);
    setDirty(false);
    setDocumentsDirty(false);
    setError("");
    api("", { signal: controller.signal })
      .then(({ order }) => {
        if (!controller.signal.aborted) accept(order);
      })
      .catch((error) => {
        if (!controller.signal.aborted) setError(premiumError(error));
      });
    return () => controller.abort();
  }, [api, accept, setError]);
  useOrderPolling(
    isProcessing(order?.status) ? `${token}:${orderId}` : null,
    async (signal) => {
      const { order: status } = await api("/status", { signal });
      if (signal.aborted) return;
      if (
        status.status !== order.status ||
        status.revision !== order.revision
      ) {
        const { order: updated } = await api("", { signal });
        if (!signal.aborted) accept(updated);
      }
      if (!signal.aborted) setError("");
    },
    (error) => setError(premiumError(error)),
  );
  const save = () => saveOrderDraft(token, order, draft);
  const openDocument = (path, saveBeforeOpening = false) =>
    run(async (isActive) => {
      if (documentsDirty) {
        if (isActive()) navigate(path);
        return;
      }
      if (dirty || saveBeforeOpening) {
        const updated = await save();
        if (!isActive()) return;
        accept(updated);
      }
      if (isActive()) navigate(path);
    });
  const refresh = () =>
    run(async (isActive) => {
      const { order: updated } = await api();
      if (isActive()) accept(updated);
    });
  const transition = (suffix, data) =>
    run(async (isActive) => {
      const { order: updated } = await api(suffix, { method: "POST", data });
      if (isActive()) accept(updated);
    });
  return {
    order,
    draft,
    dirty: dirty || documentsDirty,
    setDocumentsDirty,
    documentsUpdated: (updated, advance = false) => {
      accept(updated);
      if (advance || isProcessing(updated.status))
        navigate(premiumPaths.order(orderId));
    },
    busy,
    error,
    openDocument,
    refresh,
    onChange: (next) => {
      setDraft(next);
      setDirty(true);
    },
    preview: () => openDocument(premiumPaths.preview(orderId), true),
    approve: () =>
      transition("/approve", { revision: order.revision, confirm: true }),
    retry: () => transition("/retry", { revision: order.revision }),
    save: () =>
      run(async (isActive) => {
        const updated = await save();
        if (isActive()) {
          accept(updated);
          notify("Zapisano dane. Możesz wrócić do nich później.");
        }
      }),
  };
}
